import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Clear as ClearIcon, Search as SearchIcon } from '@mui/icons-material';
import productoService, { type Producto } from '../../services/productoService';
import {
  cargarProductoParaDocumento,
  esDiscontinuadoBloqueadoParaAgregar,
  esTipoDiscontinuado,
  evaluarUsoProductoDocumento,
  filtrarProductosInactivos,
  mensajeArticuloDiscontinuadoBloqueado,
  opcionesGetProductoDocumento,
  paramsBusquedaProductoDocumento,
  type ModoBloqueoArtDiscontinuados,
} from '../../utils/bloqueaArtDiscontinuados';
import { esEntradaCodigoBarra } from '../../utils/codigoBarraVenta';
import { formatCurrency } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';
import { getProductoFallbackImage, getProductoImageBySize } from '../../utils/productImage';

const PAGE_SIZE = 20;

const descripcionSx = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
  lineHeight: 1.35,
  wordBreak: 'break-word' as const,
};

export interface CompraArticuloSuggestion {
  codigoArticulo: string;
  descripcion: string;
  costo: number;
  um: string;
  imageUrl?: string;
  tipoArticulo?: string | null;
  cantidad?: number;
  producto?: Producto;
}

interface Props {
  nroProveedor?: number | null;
  disabled?: boolean;
  placeholder?: string;
  onAgregarProducto: (item: CompraArticuloSuggestion) => void;
}

const FacturaCompraArticulosSearchBar: React.FC<Props> = ({
  nroProveedor,
  disabled,
  placeholder = 'Código o descripción… ↑↓ y Enter',
  onAgregarProducto,
}) => {
  const [term, setTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [suggestions, setSuggestions] = useState<CompraArticuloSuggestion[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [barcodeError, setBarcodeError] = useState<string | null>(null);
  const [modoBloqueoDis, setModoBloqueoDis] = useState<ModoBloqueoArtDiscontinuados>(1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestIdRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listScrollRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  const enriquecerProductos = useCallback(
    async (items: Producto[]): Promise<CompraArticuloSuggestion[]> => {
      return Promise.all(
        items.map(async (p) => {
          const codigo = String(p.codigoArticulo ?? '').trim();
          let productoFull = p;
          try {
            productoFull = await cargarProductoParaDocumento(codigo, 'COMPRAS');
          } catch {
            /* usar datos de búsqueda */
          }

          const costo = Number(productoFull.costo ?? p.costo ?? 0) || 0;
          let um = String(
            productoFull.idUnidadMedida ?? p.idUnidadMedida ?? productoFull.um ?? p.um ?? '',
          ).trim();
          try {
            const umRes = await productoService.getUmCompra(codigo, nroProveedor || null);
            um = umRes.umCompra || um;
          } catch {
            /* conservar UM base */
          }

          return {
            codigoArticulo: codigo,
            descripcion: String(productoFull.descripcion ?? p.descripcion ?? ''),
            costo,
            um,
            imageUrl: getProductoImageBySize(productoFull, 'thumbnail'),
            tipoArticulo: productoFull.tipoArticulo ?? p.tipoArticulo ?? null,
            producto: productoFull,
          };
        }),
      );
    },
    [nroProveedor],
  );

  const resetBusqueda = useCallback(() => {
    setSuggestions([]);
    setPageNumber(1);
    setTotalCount(0);
    setOpen(false);
    setHighlightedIndex(-1);
    itemRefs.current = [];
  }, []);

  const buscarProductos = useCallback(
    async (q: string) => {
      const trimmed = q.trim();
      if (!trimmed) {
        resetBusqueda();
        return;
      }

      const requestId = ++searchRequestIdRef.current;
      setLoading(true);
      setPageNumber(1);

      try {
        const paramsBloqueo = await paramsBusquedaProductoDocumento('COMPRAS');
        setModoBloqueoDis(paramsBloqueo.modoBloqueoArtDiscontinuados ?? 1);
        const res = await productoService.getProductos({
          searchTerm: trimmed,
          pageNumber: 1,
          pageSize: PAGE_SIZE,
          ...paramsBloqueo,
        });

        if (requestId !== searchRequestIdRef.current) return;

        const items = filtrarProductosInactivos(res.items ?? []);
        const enriched = await enriquecerProductos(items);

        if (requestId !== searchRequestIdRef.current) return;

        setTotalCount(res.totalCount ?? enriched.length);
        setSuggestions(enriched);
        setOpen(enriched.length > 0);
        setHighlightedIndex(enriched.length > 0 ? 0 : -1);
        itemRefs.current = [];
      } catch {
        if (requestId !== searchRequestIdRef.current) return;
        resetBusqueda();
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [enriquecerProductos, resetBusqueda],
  );

  const cargarMas = useCallback(async () => {
    const trimmed = term.trim();
    if (!trimmed || loading || loadingMore) return;
    if (suggestions.length >= totalCount) return;

    const nextPage = pageNumber + 1;
    const requestId = searchRequestIdRef.current;
    setLoadingMore(true);

    try {
      const paramsBloqueo = await paramsBusquedaProductoDocumento('COMPRAS');
      setModoBloqueoDis(paramsBloqueo.modoBloqueoArtDiscontinuados ?? 1);
      const res = await productoService.getProductos({
        searchTerm: trimmed,
        pageNumber: nextPage,
        pageSize: PAGE_SIZE,
        ...paramsBloqueo,
      });

      if (requestId !== searchRequestIdRef.current) return;

      const items = filtrarProductosInactivos(res.items ?? []);
      if (items.length === 0) return;

      const enriched = await enriquecerProductos(items);
      if (requestId !== searchRequestIdRef.current) return;

      setTotalCount(res.totalCount ?? totalCount);
      setPageNumber(nextPage);
      setSuggestions((prev) => {
        const existing = new Set(prev.map((s) => s.codigoArticulo.trim()));
        const nuevos = enriched.filter((s) => !existing.has(s.codigoArticulo.trim()));
        return [...prev, ...nuevos];
      });
      setOpen(true);
    } catch {
      /* mantener resultados actuales */
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setLoadingMore(false);
      }
    }
  }, [term, loading, loadingMore, suggestions.length, totalCount, pageNumber, enriquecerProductos]);

  const hayMasResultados = suggestions.length < totalCount;

  const handleListScroll = useCallback(() => {
    const el = listScrollRef.current;
    if (!el || loading || loadingMore || !hayMasResultados) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
    if (nearBottom) void cargarMas();
  }, [loading, loadingMore, hayMasResultados, cargarMas]);

  useEffect(() => {
    if (!open || loading || loadingMore || !hayMasResultados) return;
    const el = listScrollRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 1) void cargarMas();
  }, [open, loading, loadingMore, hayMasResultados, suggestions.length, cargarMas]);

  useEffect(() => {
    if (highlightedIndex < 0 || loading || loadingMore || !hayMasResultados) return;
    if (highlightedIndex >= suggestions.length - 3) void cargarMas();
  }, [highlightedIndex, suggestions.length, loading, loadingMore, hayMasResultados, cargarMas]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => buscarProductos(term), 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [term, buscarProductos]);

  useEffect(() => {
    if (highlightedIndex >= 0) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = async (item: CompraArticuloSuggestion) => {
    if (esDiscontinuadoBloqueadoParaAgregar(item.tipoArticulo, modoBloqueoDis)) {
      setBarcodeError(mensajeArticuloDiscontinuadoBloqueado(item.codigoArticulo));
      return;
    }
    const decision = await evaluarUsoProductoDocumento(
      {
        codigoArticulo: item.codigoArticulo,
        tipoArticulo: item.tipoArticulo,
        descripcion: item.descripcion,
      },
      'COMPRAS',
    );
    if (!decision.permitido) {
      setBarcodeError(decision.mensaje);
      return;
    }
    onAgregarProducto(item);
    setTerm('');
    setBarcodeError(null);
    resetBusqueda();
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const resolverYElegir = useCallback(
    async (codigo: string) => {
      setLoading(true);
      setBarcodeError(null);
      setOpen(false);
      try {
        const apiOpts = await opcionesGetProductoDocumento('COMPRAS');
        const resolved = await productoService.resolverCodigoBarra(codigo, undefined, apiOpts);
        const tipoResuelto = (resolved.producto as { tipoArticulo?: string | null } | undefined)
          ?.tipoArticulo;
        const decisionBarra = await evaluarUsoProductoDocumento(
          {
            codigoArticulo: resolved.codigoArticulo,
            tipoArticulo: tipoResuelto,
            descripcion: resolved.descripcion,
          },
          'COMPRAS',
        );
        if (!decisionBarra.permitido) {
          setBarcodeError(decisionBarra.mensaje);
          return;
        }

        let um = String(resolved.um ?? '').trim();
        const productoResuelto = resolved.producto as Producto | undefined;
        const costo = Number(productoResuelto?.costo ?? resolved.precioUnitario ?? 0) || 0;
        try {
          const umRes = await productoService.getUmCompra(
            String(resolved.codigoArticulo),
            nroProveedor || null,
          );
          um = umRes.umCompra || um;
        } catch {
          /* conservar UM */
        }

        onAgregarProducto({
          codigoArticulo: resolved.codigoArticulo,
          descripcion: resolved.descripcion,
          costo,
          um,
          cantidad: resolved.cantidad,
          tipoArticulo: tipoResuelto,
          producto: productoResuelto,
          imageUrl: getProductoImageBySize(productoResuelto, 'thumbnail'),
        });
        setTerm('');
        resetBusqueda();
        setTimeout(() => inputRef.current?.focus(), 50);
      } catch (err) {
        setBarcodeError(getApiErrorMessage(err, 'No se pudo resolver el código de barras.'));
      } finally {
        setLoading(false);
      }
    },
    [nroProveedor, onAgregarProducto, resetBusqueda],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    if (e.key === 'Enter') {
      const trimmed = term.trim();
      if (esEntradaCodigoBarra(trimmed)) {
        e.preventDefault();
        void resolverYElegir(trimmed);
        return;
      }
    }

    if (suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlightedIndex((prev) => {
        if (prev < 0) return 0;
        return prev < suggestions.length - 1 ? prev + 1 : 0;
      });
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setHighlightedIndex((prev) => {
        if (prev < 0) return suggestions.length - 1;
        return prev > 0 ? prev - 1 : suggestions.length - 1;
      });
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
      const item = suggestions[idx];
      if (item) void handleSelect(item);
    }
  };

  const handleClear = () => {
    setTerm('');
    setBarcodeError(null);
    resetBusqueda();
    inputRef.current?.focus();
  };

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      <TextField
        inputRef={inputRef}
        fullWidth
        placeholder={placeholder}
        value={term}
        onChange={(e) => {
          setTerm(e.target.value);
          if (barcodeError) setBarcodeError(null);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        disabled={disabled}
        size="small"
        error={Boolean(barcodeError)}
        helperText={barcodeError ?? undefined}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {loading ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" color="action" />}
            </InputAdornment>
          ),
          endAdornment: term ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClear} edge="end">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
      />

      {open && suggestions.length > 0 && (
        <Paper
          ref={listScrollRef}
          onScroll={handleListScroll}
          elevation={6}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1200,
            maxHeight: 340,
            overflowY: 'auto',
            mt: 0.5,
            borderRadius: 2,
          }}
        >
          <List dense disablePadding>
            {suggestions.map((s, idx) => {
              const disBloqueado = esDiscontinuadoBloqueadoParaAgregar(s.tipoArticulo, modoBloqueoDis);
              const esDis = esTipoDiscontinuado(s.tipoArticulo);
              return (
                <ListItem
                  key={s.codigoArticulo}
                  disablePadding
                  divider={idx < suggestions.length - 1 || loadingMore}
                >
                  <ListItemButton
                    ref={(el) => {
                      itemRefs.current[idx] = el;
                    }}
                    selected={idx === highlightedIndex}
                    disabled={disBloqueado}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    onClick={() => void handleSelect(s)}
                    sx={{
                      py: 0.75,
                      px: 1.5,
                      display: 'flex',
                      alignItems: 'stretch',
                      gap: 1.25,
                      opacity: disBloqueado ? 0.72 : 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        flexShrink: 0,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        bgcolor: 'grey.50',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box
                        component="img"
                        src={s.imageUrl ?? getProductoFallbackImage()}
                        alt=""
                        sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        onError={(e) => {
                          e.currentTarget.src = getProductoFallbackImage();
                        }}
                      />
                    </Box>
                    <Box flex={1} minWidth={0}>
                      <Tooltip
                        title={
                          disBloqueado
                            ? mensajeArticuloDiscontinuadoBloqueado(s.codigoArticulo)
                            : s.descripcion
                        }
                        placement="top-start"
                        enterDelay={400}
                        leaveDelay={0}
                        disableInteractive
                      >
                        <Typography variant="body2" component="span" sx={descripcionSx}>
                          {s.descripcion}
                        </Typography>
                      </Tooltip>
                      <Box display="flex" alignItems="center" gap={1} mt={0.35} flexWrap="wrap">
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace', flexShrink: 0, minWidth: 48 }}
                        >
                          {s.codigoArticulo}
                        </Typography>
                        <Typography variant="caption" color="primary.main" fontWeight={600}>
                          {formatCurrency(s.costo)}
                        </Typography>
                        {s.um && (
                          <Typography variant="caption" color="text.secondary">
                            / {s.um}
                          </Typography>
                        )}
                        {esDis ? (
                          <Chip
                            label="Discontinuado"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        ) : null}
                      </Box>
                    </Box>
                  </ListItemButton>
                </ListItem>
              );
            })}
            {loadingMore && (
              <ListItem disablePadding>
                <Box sx={{ py: 1.25, display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <CircularProgress size={18} />
                </Box>
              </ListItem>
            )}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default FacturaCompraArticulosSearchBar;
